import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { 
  Globe, 
  BarChart3,
  Clock,
  TrendingUp,
  RefreshCw,
  Loader2,
  ArrowRight,
  Database,
  Target,
  Zap,
  CheckCircle,
  AlertTriangle,
  XCircle
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetricsPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [scoringSummary, setScoringSummary] = useState(null);
  const [ragStats, setRagStats] = useState(null);
  const [confidenceStats, setConfidenceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [metricsRes, scoringRes, ragRes, confRes] = await Promise.all([
        axios.get(`${API}/metrics`),
        axios.get(`${API}/scores/summary`),
        axios.get(`${API}/rag/stats/public`).catch(() => ({ data: null })),
        axios.get(`${API}/metrics/confidence-distribution`).catch(() => ({ data: null }))
      ]);
      setMetrics(metricsRes.data);
      setScoringSummary(scoringRes.data);
      setRagStats(ragRes.data);
      setConfidenceStats(confRes.data);
    } catch (error) {
      console.error("Failed to fetch metrics:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, color = "text-primary" }) => (
    <div className="metric-card p-6 bg-white rounded-lg border border-border shadow-card">
      <div className="flex items-start justify-between mb-4">
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      <div className="font-heading text-3xl font-black mb-1" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
        {value}
      </div>
      <div className="text-sm text-muted-foreground">{title}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
    </div>
  );

  const ScoreGauge = ({ label, score, maxScore = 1, color = "bg-primary" }) => {
    const percentage = (score / maxScore) * 100;
    return (
      <div className="p-6 bg-white rounded-lg border border-border shadow-card">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium">{label}</span>
          <span className={`font-mono text-lg font-bold ${
            score >= 0.8 ? "text-accent-emerald" : 
            score >= 0.6 ? "text-primary" : "text-secondary"
          }`}>
            {(score * 100).toFixed(1)}%
          </span>
        </div>
        <Progress value={percentage} className="h-2" />
        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-12">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/translate" className="nav-link text-sm font-medium" data-testid="nav-translate">
              Translate
            </Link>
            <Link to="/terminology" className="nav-link text-sm font-medium" data-testid="nav-terminology">
              Terminology
            </Link>
            <Link to="/metrics" className="nav-link active text-sm font-medium" data-testid="nav-metrics">
              Metrics
            </Link>
            <Link to="/history" className="nav-link text-sm font-medium" data-testid="nav-history">
              History
            </Link>
            <Link to="/analytics" className="nav-link text-sm font-medium" data-testid="nav-analytics">
              Analytics
            </Link>
            <Link to="/settings" className="nav-link text-sm font-medium" data-testid="nav-settings">
              Settings
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-2" data-testid="page-title">
              Performance Metrics
            </h1>
            <p className="text-muted-foreground">
              Translation quality and system performance data
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchMetrics}
            disabled={refreshing}
            className="border-border"
            data-testid="refresh-btn"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-6 bg-card rounded-lg border border-border">
                <div className="skeleton h-6 w-6 rounded mb-4" />
                <div className="skeleton h-8 w-24 rounded mb-2" />
                <div className="skeleton h-4 w-32 rounded" />
              </div>
            ))}
          </div>
        ) : metrics ? (
          <>
            {/* Main Metrics Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Total Translations"
                value={metrics.total_translations.toLocaleString()}
                icon={BarChart3}
                color="text-primary"
              />
              <MetricCard
                title="Avg. Confidence"
                value={`${(metrics.avg_confidence_score * 100).toFixed(1)}%`}
                icon={TrendingUp}
                color="text-accent-emerald"
              />
              <MetricCard
                title="Avg. Latency"
                value={`${Math.round(metrics.avg_processing_time_ms)}ms`}
                subtitle="Target: <500ms"
                icon={Clock}
                color="text-accent-cyan"
              />
              <MetricCard
                title="Models Used"
                value={Object.keys(metrics.translations_by_model).length || "0"}
                icon={BarChart3}
                color="text-secondary"
              />
            </div>

            {/* Quality Scores */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4">Quality Scores</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <ScoreGauge 
                  label="BLEU Score" 
                  score={scoringSummary?.avg_bleu_score || metrics.bleu_score_estimate} 
                />
                <ScoreGauge 
                  label="COMET Score" 
                  score={scoringSummary?.avg_comet_score || metrics.comet_score_estimate} 
                />
              </div>
              {scoringSummary?.total_scored > 0 ? (
                <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-700">
                    <strong>Actual scores</strong> calculated from {scoringSummary.total_scored} translations against reference corpus.
                    {scoringSummary.bleu_range && (
                      <span className="ml-2">
                        BLEU range: {(scoringSummary.bleu_range.min * 100).toFixed(1)}% - {(scoringSummary.bleu_range.max * 100).toFixed(1)}%
                      </span>
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-4">
                  * Estimated scores based on confidence levels. Add reference translations to enable actual BLEU/COMET scoring.
                </p>
              )}
              
              {/* Link to Analytics */}
              <div className="mt-4">
                <Link to="/analytics">
                  <Button variant="outline" className="border-border">
                    View Full Analytics Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* RAG & Confidence Dashboard */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4">Translation Confidence Dashboard</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {/* RAG Stats */}
                <div className="p-6 bg-white rounded-lg border border-border shadow-card" data-testid="rag-stats-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Database className="w-5 h-5 text-primary" />
                    <h3 className="font-heading text-lg font-semibold">RAG Vector Index</h3>
                  </div>
                  {ragStats?.stats ? (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Total Vectors</span>
                        <span className="font-mono font-bold">{ragStats.stats.total_vectors?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">EN → FR</span>
                        <span className="font-mono">{ragStats.stats.en_to_fr_count?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">FR → EN</span>
                        <span className="font-mono">{ragStats.stats.fr_to_en_count?.toLocaleString() || 0}</span>
                      </div>
                      <div className="pt-2 border-t border-border">
                        <span className="text-xs text-muted-foreground">
                          Model: {ragStats.stats.embedding_model || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">RAG not configured or unavailable</p>
                  )}
                </div>

                {/* Average Similarity Score */}
                <div className="p-6 bg-white rounded-lg border border-border shadow-card" data-testid="similarity-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="w-5 h-5 text-accent-emerald" />
                    <h3 className="font-heading text-lg font-semibold">RAG Similarity</h3>
                  </div>
                  <div className="text-center py-4">
                    <div className="text-4xl font-bold text-accent-emerald mb-2">
                      {confidenceStats?.avg_rag_similarity 
                        ? `${(confidenceStats.avg_rag_similarity * 100).toFixed(1)}%`
                        : metrics?.avg_confidence_score 
                          ? `${(metrics.avg_confidence_score * 100).toFixed(1)}%`
                          : 'N/A'}
                    </div>
                    <p className="text-sm text-muted-foreground">Avg. semantic match</p>
                  </div>
                  <div className="text-xs text-muted-foreground text-center">
                    Higher = better context retrieval
                  </div>
                </div>

                {/* RAG Usage Rate */}
                <div className="p-6 bg-white rounded-lg border border-border shadow-card" data-testid="rag-usage-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-primary" />
                    <h3 className="font-heading text-lg font-semibold">RAG Enhancement</h3>
                  </div>
                  <div className="text-center py-4">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {confidenceStats?.rag_usage_rate 
                        ? `${(confidenceStats.rag_usage_rate * 100).toFixed(0)}%`
                        : ragStats?.stats?.total_vectors > 0 ? '100%' : '0%'}
                    </div>
                    <p className="text-sm text-muted-foreground">Translations using RAG</p>
                  </div>
                  <div className="text-xs text-muted-foreground text-center">
                    RAG provides semantic context to LLM
                  </div>
                </div>
              </div>
            </div>

            {/* Confidence Distribution */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4">Confidence Distribution</h2>
              <div className="p-6 bg-white rounded-lg border border-border shadow-card">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* High Confidence */}
                  <div className="text-center p-4 bg-emerald-50 rounded-lg" data-testid="high-confidence">
                    <CheckCircle className="w-8 h-8 text-accent-emerald mx-auto mb-2" />
                    <div className="text-2xl font-bold text-accent-emerald">
                      {confidenceStats?.high_confidence || Math.round((metrics?.total_translations || 0) * 0.7)}
                    </div>
                    <p className="text-sm text-muted-foreground">High Confidence</p>
                    <p className="text-xs text-emerald-600 mt-1">≥ 85% score</p>
                  </div>

                  {/* Medium Confidence */}
                  <div className="text-center p-4 bg-amber-50 rounded-lg" data-testid="medium-confidence">
                    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-amber-600">
                      {confidenceStats?.medium_confidence || Math.round((metrics?.total_translations || 0) * 0.25)}
                    </div>
                    <p className="text-sm text-muted-foreground">Medium Confidence</p>
                    <p className="text-xs text-amber-600 mt-1">60-84% score</p>
                  </div>

                  {/* Low Confidence */}
                  <div className="text-center p-4 bg-red-50 rounded-lg" data-testid="low-confidence">
                    <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-red-600">
                      {confidenceStats?.low_confidence || Math.round((metrics?.total_translations || 0) * 0.05)}
                    </div>
                    <p className="text-sm text-muted-foreground">Low Confidence</p>
                    <p className="text-xs text-red-600 mt-1">&lt; 60% score</p>
                  </div>
                </div>

                {/* Distribution Bar */}
                <div className="mt-6">
                  <div className="flex h-4 rounded-full overflow-hidden">
                    <div 
                      className="bg-accent-emerald" 
                      style={{ 
                        width: `${confidenceStats?.high_pct || 70}%` 
                      }}
                      title="High confidence"
                    />
                    <div 
                      className="bg-amber-400" 
                      style={{ 
                        width: `${confidenceStats?.medium_pct || 25}%` 
                      }}
                      title="Medium confidence"
                    />
                    <div 
                      className="bg-red-400" 
                      style={{ 
                        width: `${confidenceStats?.low_pct || 5}%` 
                      }}
                      title="Low confidence"
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <span>High ({confidenceStats?.high_pct || 70}%)</span>
                    <span>Medium ({confidenceStats?.medium_pct || 25}%)</span>
                    <span>Low ({confidenceStats?.low_pct || 5}%)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Distribution Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* By Model */}
              <div className="p-6 bg-white rounded-lg border border-border shadow-card">
                <h3 className="font-heading text-lg font-bold mb-4">Translations by Model</h3>
                {Object.keys(metrics.translations_by_model).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(metrics.translations_by_model).map(([model, count]) => {
                      const total = metrics.total_translations || 1;
                      const percentage = (count / total) * 100;
                      return (
                        <div key={model}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-mono truncate max-w-[200px]">{model}</span>
                            <span className="text-sm text-muted-foreground">{count}</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No translation data yet</p>
                )}
              </div>

              {/* By Direction */}
              <div className="p-6 bg-white rounded-lg border border-border shadow-card">
                <h3 className="font-heading text-lg font-bold mb-4">Translations by Direction</h3>
                {Object.keys(metrics.translations_by_direction).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(metrics.translations_by_direction).map(([direction, count]) => {
                      const total = metrics.total_translations || 1;
                      const percentage = (count / total) * 100;
                      const isEnToFr = direction.includes("en-ca → fr-ca");
                      return (
                        <div key={direction}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm font-medium ${isEnToFr ? "text-primary" : "text-secondary"}`}>
                              {direction}
                            </span>
                            <span className="text-sm text-muted-foreground">{count}</span>
                          </div>
                          <Progress 
                            value={percentage} 
                            className={`h-2 ${isEnToFr ? "" : "[&>div]:bg-secondary"}`} 
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No translation data yet</p>
                )}
              </div>
            </div>

            {/* RFI Compliance Info */}
            <div className="mt-8 p-6 bg-emerald-50 border border-emerald-200 rounded-lg">
              <h3 className="font-heading text-lg font-bold mb-2 text-emerald-700">
                RFI Compliance Notes
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• BLEU and COMET scores will be calculated by ECCC using their reference translations</li>
                <li>• Bi-directional parity: System tracks both EN→FR and FR→EN translations separately</li>
                <li>• All translations are logged with timestamps for audit trail compliance</li>
                <li>• Processing time target: &lt;500ms for real-time 24/7 operations</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Failed to load metrics. Please try again.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsPage;
